import { ReactNode, useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Flex, Separator, Spinner, Button, ScrollArea } from "@radix-ui/themes";
import { ChevronLeft, Link as LinkIcon } from "lucide-react";
import { Dialog } from "@/components/shared/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import { useTicketViewApi } from "@/v2/entities/ticket-view/TicketView.api";
import {
  ExploreCustomField,
  ExploreEnrichment,
  ExploreInteractionRow,
} from "@/v2/entities/explore/Explore.model";
import { Overview } from "./overview/Overview";
import { Evaluation } from "./evaluation/Evaluation";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { apiResolver, useApi } from "@/api/useApi";
import { CommentValue } from "@/api/ticket-transcript/TicketTranscript.model";
import { TicketTranscript } from "@/features/ticket-transcript/TicketTranscript";
import { OverviewIcon } from "./assets/OverviewIcon";
import { EvaluationIcon } from "./assets/EvaluationIcon";
import { InteractionViewNavbar } from "./parts/InteractionViewNavbar";
import { InteractionViewContent } from "./parts/InteractionViewContent";
import { InteractionViewHeader } from "./parts/InteractionViewHeader";
import { InteractionViewTabContent } from "./parts/InteractionViewTabContent";
import { ActivityIcon } from "./assets/ActivityIcon";
import { TrackerIcon } from "./assets/TracketIcon";
import { useInteractionEventApi } from "@/v2/entities/interaction-event/InteractionEvent.api";
import { InteractionEvent } from "./interaction-event/InteractionEvent";
import { useTrackerApi } from "@/v2/entities/tracker/Tracker.api";
import { Tracker } from "./tracker/Tracker";
import { SendTicketToTraining } from "./training/SendTicketToTraining";
import { MarkAsValidated } from "./training/MarkAsValidated";
import { useReleases } from "@/routes/simulator/context/release";
import Select from "@/components/shared/select";
import ChainOfThoughts from "@/v2/features/simulator/parts/chainOfThoughts/ChainOfThoughts";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { Comment } from "@/features/ticket-transcript/Comment";
import { SimulatorDistinctVersions } from "../simulator/Simulator.versions";
import { useEnrichmentApi } from "@/v2/entities/enrichment/Enrichment.api";
import { toast } from "sonner";
import {
  convertAiAgentStepsToChainOfThoughtSteps,
  convertSuggestedReplyToolCallsToChainOfThoughtSteps,
} from "@/v2/utils/aiAgentStepsConverter";

export type TicketTranscriptResponse = {
  comments: CommentValue[];
};

export type TicketViewTab = "overview" | "evaluation" | "activity" | "tracker";

interface TicketViewTabItem {
  label: string;
  value: TicketViewTab;
  icon?: ReactNode;
  isLoading?: boolean;
}

interface TicketViewContentProps {
  ticket: ExploreInteractionRow;
  enrichments: ExploreEnrichment[];
  customFields: ExploreCustomField[];
  allowSendToTraining?: boolean;
  allowValidate?: boolean;
  onTicketValidated?: () => void;
  hideTranscript?: boolean;
}

export function TicketViewContent({
  ticket,
  enrichments,
  customFields,
  allowSendToTraining = true,
  allowValidate = false,
  onTicketValidated,
  hideTranscript = false,
}: TicketViewContentProps) {
  const queryClient = useQueryClient();
  const { api } = useApi();
  const ticketViewApi = useTicketViewApi();
  const trackerApi = useTrackerApi();
  const interactionEventApi = useInteractionEventApi();
  const enrichmentApi = useEnrichmentApi();
  const { simulators, loadingSimulators } = useReleases();
  const [selectedTab, setSelectedTab] = useState<TicketViewTab>("overview");
  const [selectedSimulatorId, setSelectedSimulatorId] = useState<string>("");
  const [isSimulatorModalOpen, setIsSimulatorModalOpen] = useState(false);
  const [isCreatingEnrichment, setIsCreatingEnrichment] = useState(false);
  const [showChainOfThoughts, setShowChainOfThoughts] = useState(false);
  const [chainOfThoughtsSource, setChainOfThoughtsSource] = useState<any>(null);

  const evaluationQuery = useQuery({
    queryKey: ["ticket-view", "evaluation", ticket.id],
    queryFn: () => ticketViewApi.getTicketEvaluation(ticket.id),
  });
  const transcriptQuery = useQuery({
    queryKey: ["ticket-view", "transcript", ticket.id],
    queryFn: () =>
      apiResolver<TicketTranscriptResponse>(
        api.get(
          `/platform/tickets/${ticket.id}/comments?includeContextExport=true&includeChainOfThoughts=true`
        )
      ),
  });
  const trackerQuery = useQuery({
    queryKey: ["ticket-view", "tracker", ticket.id],
    queryFn: () => trackerApi.getTrackerItemBySource("tickets", ticket.id),
    enabled: true,
  });
  const activityQuery = useQuery({
    queryKey: ["ticket-view", "activity", ticket.id],
    queryFn: () => interactionEventApi.getInteractionEvents(ticket.id, true),
    enabled: true,
  });
  const insightsQuery = useQuery({
    queryKey: ["ticket-view", "insights", ticket.id],
    queryFn: () => ticketViewApi.getTicketInsights(ticket.id),
    select: (data) => {
      const latestInsight = data?.sort((a, b) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })[0];

      if (latestInsight == null) {
        return;
      }

      const tldr = latestInsight.tldr;

      if (tldr == null || tldr === "") {
        return;
      }

      return tldr;
    },
  });

  const tabList = useMemo<TicketViewTabItem[]>(() => {
    const tabs: TicketViewTabItem[] = [
      { label: "Overview", value: "overview", icon: <OverviewIcon /> },
    ];
    tabs.push({
      label: "Activity",
      value: "activity",
      icon: <ActivityIcon />,
      isLoading: activityQuery.isLoading,
    });
    tabs.push({
      label: "Evaluation",
      value: "evaluation",
      icon: <EvaluationIcon />,
      isLoading: evaluationQuery.isLoading,
    });
    tabs.push({
      label: "Tracker",
      value: "tracker",
      icon: <TrackerIcon />,
      isLoading: trackerQuery.isLoading,
    });
    return tabs;
  }, [
    evaluationQuery.isLoading,
    activityQuery.isLoading,
    trackerQuery.isLoading,
  ]);

  const handleUpdateTicketScore = useCallback(async () => {
    await Promise.all([
      evaluationQuery.refetch(),
      queryClient.invalidateQueries(["explore-v2"]),
    ]);
  }, [evaluationQuery.refetch, queryClient]);

  const handleSimulatorSelect = useCallback((simulatorId: string) => {
    setSelectedSimulatorId(simulatorId);
    setIsSimulatorModalOpen(true);
  }, []);

  const handleSimulatorModalClose = useCallback(() => {
    setIsSimulatorModalOpen(false);
    setSelectedSimulatorId("");
  }, []);

  const handleCreateEnrichment = useCallback(
    async (data: { task: string; name: string; apiResponse: any }) => {
      setIsCreatingEnrichment(true);

      try {
        await enrichmentApi.create({
          name: data.name,
          task: data.task,
          filterRules: "{}",
          options: JSON.stringify(data.apiResponse.options || []),
          isEnabled: false, // Start disabled by default
          trigger: data.apiResponse.trigger || "ON_FINITE_STATE",
          output: JSON.stringify(data.apiResponse.output || []),
          sources: data.apiResponse.sources || [],
        });

        toast(
          "enrichment created successfully and will be available on interactions from now on"
        );
      } catch (err: any) {
        toast.error("Failed to create enrichment");
        throw err; // Re-throw to let the popover handle it
      } finally {
        setIsCreatingEnrichment(false);
      }
    },
    [enrichmentApi]
  );

  const isTrackerPage = useMemo(() => {
    return window.location.pathname.includes("/trackers");
  }, []);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  }, []);

  return (
    <>
      <Flex
        height={"100%"}
        pt={"1"}
        px={"1"}
        direction={"column"}
        overflow={"hidden"}
      >
        <InteractionViewNavbar />

        <ResizablePanelGroup
          direction="horizontal"
          className="h-full w-full overflow-hidden"
        >
          <ResizablePanel defaultSize={68} minSize={30}>
            <InteractionViewContent>
              <InteractionViewHeader
                interaction={ticket}
                rightAction={
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={handleCopyLink}
                    aria-label="Copy link"
                  >
                    <LinkIcon size={16} />
                  </Button>
                }
              />

              <Flex pr={"24px"}>
                <Separator orientation="horizontal" size={"4"} />
              </Flex>

              <Flex
                height={"100%"}
                pt={"4"}
                direction={"column"}
                overflow={"hidden"}
              >
                <Flex px={"28px"} py={"3"} justify={"between"} align={"center"}>
                  <Tabs
                    value={selectedTab}
                    onValueChange={(value) =>
                      setSelectedTab(value as TicketViewTab)
                    }
                  >
                    <TabsList size={"md"}>
                      {tabList.map((tab) => (
                        <TabsTrigger
                          key={tab.value}
                          value={tab.value}
                          size={"md"}
                          translate={tab.isLoading ? "no" : "yes"}
                        >
                          {tab.isLoading ? <Spinner size={"2"} /> : tab.icon}
                          {tab.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>

                  <Flex gap={"2"}>
                    {allowValidate && selectedTab === "evaluation" && (
                      <MarkAsValidated
                        interactionId={ticket.id}
                        onValidated={onTicketValidated}
                        source="tickets"
                      />
                    )}

                    {allowSendToTraining && (
                      <SendTicketToTraining
                        source="tickets"
                        interaction={ticket}
                      />
                    )}

                    {isTrackerPage && (
                      <Select
                        value={selectedSimulatorId}
                        size="small"
                        onChange={handleSimulatorSelect}
                        disabled={loadingSimulators}
                        placeholder="Train in Simulator"
                        options={
                          simulators?.map((simulator) => ({
                            label: simulator.name,
                            value: simulator.id.toString(),
                          })) ?? []
                        }
                        classes={{
                          trigger: "w-[200px]",
                        }}
                      />
                    )}
                  </Flex>
                </Flex>

                {selectedTab === "overview" && (
                  <InteractionViewTabContent>
                    <Overview
                      source="tickets"
                      interaction={ticket}
                      enrichments={enrichments}
                      customFields={customFields}
                      summary={insightsQuery.data}
                      loadingSummary={insightsQuery.isLoading}
                      onCreateEnrichment={handleCreateEnrichment}
                      isCreatingEnrichment={isCreatingEnrichment}
                    />
                  </InteractionViewTabContent>
                )}

                {selectedTab === "activity" && (
                  <InteractionViewTabContent
                    isLoading={activityQuery.isLoading}
                    isError={activityQuery.isError}
                    refetch={activityQuery.refetch}
                  >
                    <InteractionEvent data={activityQuery.data ?? []} />
                  </InteractionViewTabContent>
                )}

                {selectedTab === "evaluation" && (
                  <InteractionViewTabContent
                    isError={evaluationQuery.isError}
                    refetch={evaluationQuery.refetch}
                  >
                    <Evaluation
                      source="tickets"
                      interaction={ticket}
                      scores={evaluationQuery.data?.scores ?? []}
                      collaborators={evaluationQuery.data?.collaborators ?? []}
                      isLoading={evaluationQuery.isLoading}
                      onUpdateInteractionScore={handleUpdateTicketScore}
                    />
                  </InteractionViewTabContent>
                )}

                {selectedTab === "tracker" && (
                  <InteractionViewTabContent
                    isLoading={trackerQuery.isLoading}
                    isError={trackerQuery.isError}
                    refetch={trackerQuery.refetch}
                  >
                    <Tracker
                      data={trackerQuery.data ?? []}
                      refetch={trackerQuery.refetch}
                    />
                  </InteractionViewTabContent>
                )}
              </Flex>
            </InteractionViewContent>
          </ResizablePanel>

          {!hideTranscript && (
            <>
              <ResizableHandle withHandle />

              <ResizablePanel defaultSize={32} minSize={10}>
                <Flex
                  height={"100%"}
                  pt={"32px"}
                  direction={"column"}
                  overflow={"hidden"}
                >
                  <TicketTranscript
                    data={transcriptQuery.data?.comments}
                    isLoading={transcriptQuery.isLoading}
                    collaborators={[]}
                    onChainOfThoughtsClick={(comment) => {
                      const chainOfThoughtSteps =
                        comment.suggestedReplies?.[0]?.aiResponseThoughts?.[0]
                          ?.steps || [];

                      const aiAgentSteps =
                        comment.suggestedReplies?.[0]?.steps || [];
                      const convertedToolSteps =
                        convertAiAgentStepsToChainOfThoughtSteps(aiAgentSteps);

                      const suggestedReplyToolCalls =
                        comment.suggestedReplies?.[0]?.toolCalls || [];
                      const convertedSuggestedReplyToolSteps =
                        convertSuggestedReplyToolCallsToChainOfThoughtSteps(
                          suggestedReplyToolCalls
                        );

                      const allSteps = [
                        ...chainOfThoughtSteps,
                        ...convertedToolSteps,
                        ...convertedSuggestedReplyToolSteps,
                      ];

                      setChainOfThoughtsSource({
                        comment,
                        chainOfThoughtSteps: allSteps,
                        contextExport:
                          comment.suggestedReplies?.[0]?.contextExport || [],
                      });
                      setShowChainOfThoughts(true);
                    }}
                  />
                </Flex>
              </ResizablePanel>
            </>
          )}
          {showChainOfThoughts && chainOfThoughtsSource && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={30} minSize={15}>
                <ScrollArea scrollbars="vertical">
                  <div className="h-full my-8">
                    <Button
                      className="absolute top-2.5 left-2.5"
                      variant="ghost"
                      size="1"
                      onClick={() => setShowChainOfThoughts(false)}
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <div className="mb-4">
                      {chainOfThoughtsSource.comment ? (
                        <Comment
                          authorName={chainOfThoughtsSource.comment.authorName}
                          createdAt={chainOfThoughtsSource.comment.createdAt}
                          updatedAt={chainOfThoughtsSource.comment.createdAt}
                          authorId={chainOfThoughtsSource.comment.externalId}
                          body={chainOfThoughtsSource.comment.body}
                          isInternal={chainOfThoughtsSource.comment.isInternal}
                          agent={chainOfThoughtsSource.comment.agent}
                          externalId={chainOfThoughtsSource.comment.externalId}
                          color={chainOfThoughtsSource.comment.color}
                          hideHeader={false}
                          hasConscutiveComment={false}
                          suggestedReplies={[]}
                        />
                      ) : (
                        <div className="mb-4 flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold">
                              Chain of thought
                            </h3>
                          </div>
                        </div>
                      )}
                    </div>
                    <Flex direction="column" px="6">
                      <ChainOfThoughts
                        title="Chain of thought"
                        steps={chainOfThoughtsSource.chainOfThoughtSteps}
                        defaultOpen={true}
                        currentStepIndex={-1}
                        sources={
                          Array.isArray(chainOfThoughtsSource.contextExport)
                            ? chainOfThoughtsSource.contextExport
                            : []
                        }
                        isLoading={false}
                        hasTopic={true}
                        collapsible={false}
                      />
                    </Flex>
                  </div>
                </ScrollArea>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </Flex>

      {isSimulatorModalOpen && selectedSimulatorId && (
        <Dialog
          open={isSimulatorModalOpen}
          onOpenChange={setIsSimulatorModalOpen}
        >
          <Dialog.Content size="large" className="rounded-lg h-[980px]">
            <Dialog.Title>
              Simulator Training
              <Dialog.Close onClick={handleSimulatorModalClose} />
            </Dialog.Title>
            <SimulatorDistinctVersions
              simulatorId={selectedSimulatorId}
              hideNavbar={true}
              hideTopicsList={true}
              selectedTicketId={ticket.id}
              ticketDescription={ticket.description}
              onCloseSimulator={handleSimulatorModalClose}
            />
          </Dialog.Content>
        </Dialog>
      )}
    </>
  );
}

interface TicketViewProps extends TicketViewContentProps {
  onClose: () => void;
}

export function TicketView({ onClose, ...contentProps }: TicketViewProps) {
  const handleOpenChanged = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Dialog open onOpenChange={handleOpenChanged}>
      <Dialog.Content
        size={"large"}
        className="rounded-lg h-[980px]"
        aria-describedby={undefined}
      >
        <Dialog.Title>Ticket</Dialog.Title>
        <Dialog.Close className="absolute top-[14px] right-[14px]" />

        <TicketViewContent {...contentProps} />
      </Dialog.Content>
    </Dialog>
  );
}
